import {
  Blocks,
  Brain,
  ChartColumn,
  Cpu,
  Globe,
  MessageCircle,
  Monitor,
  Phone,
  Send,
  Shapes,
  Smartphone,
} from 'lucide-react';
import PageHeader from '../../components/site/PageHeader';
import SEO from '../../components/SEO';
import { seoConfig } from '../../configs/seoConfig';
import contactInfo, { getWhatsAppUrl } from '../../configs/contactInfo';
import { useSiteConfig } from '../../configs/siteConfig';
import { ENQUIRY_TYPES } from '../../services/enquiryService';
import {
  EnquirySent,
  Field,
  Honeypot,
  LIMITS,
  supportHours,
  useEnquiryForm,
  validateCommon,
} from './enquiryForm';
import './RequestProjectPage.css';

// Options are sent as their labels so the admin inbox reads naturally.
const PROJECT_TYPES = [
  { label: 'Web application', icon: Globe },
  { label: 'Mobile app', icon: Smartphone },
  { label: 'Machine learning / AI', icon: Brain },
  { label: 'Data science', icon: ChartColumn },
  { label: 'IoT / hardware', icon: Cpu },
  { label: 'Blockchain', icon: Blocks },
  { label: 'Desktop application', icon: Monitor },
  { label: 'Something else', icon: Shapes },
];

const BUDGETS = [
  'Under ₹2,000',
  '₹2,000–₹5,000',
  '₹5,000–₹10,000',
  '₹10,000–₹25,000',
  'Above ₹25,000',
  'Not sure yet',
];

const TIMELINES = [
  'Within 1 week',
  '1–2 weeks',
  '2–4 weeks',
  '1–2 months',
  'Flexible',
];

const INITIAL_VALUES = {
  name: '',
  email: '',
  phone: '',
  projectType: '',
  budget: '',
  timeline: '',
  subject: '',
  message: '',
};

const validate = values => {
  const errors = validateCommon(values);
  if (!values.projectType)
    errors.projectType = 'Choose the kind of project you need.';
  // Keep the on-screen order: errors focus the first invalid field.
  const order = ['name', 'email', 'phone', 'projectType', 'subject', 'message'];
  return Object.fromEntries(
    order.filter(key => errors[key]).map(key => [key, errors[key]])
  );
};

const toPayload = values => ({
  name: values.name.trim(),
  email: values.email.trim(),
  phone: values.phone.trim() || undefined,
  subject: values.subject.trim() || `${values.projectType} request`,
  message: values.message.trim(),
  projectType: values.projectType,
  budget: values.budget || undefined,
  timeline: values.timeline || undefined,
});

const RequestProjectPage = () => {
  useSiteConfig();
  const WHATSAPP_URL = getWhatsAppUrl(
    `Hi ${contactInfo.companyName}, I'd like to request a custom project.`
  );
  const form = useEnquiryForm({
    type: ENQUIRY_TYPES.PROJECT,
    initialValues: INITIAL_VALUES,
    validate,
    toPayload,
  });
  const { errors, status, submitError, field } = form;
  const sending = status === 'sending';

  return (
    <main className="pk-page">
      <SEO {...seoConfig.pages.requestProject} />
      <div className="pk-container">
        <PageHeader
          title="Request a project"
          subtitle={`Need a project that isn't in the catalog? Tell us what you need and we'll get back to you, ${supportHours()}.`}
          crumbs={[{ label: 'Home', to: '/' }, { label: 'Request a project' }]}
        />

        <div className="pk-split">
          <section className="pk-card" aria-labelledby="rq-form-title">
            {status === 'sent' ? (
              <EnquirySent
                title="Request sent"
                text={`We've received your request. We reply ${supportHours()}.`}
                onReset={form.reset}
                resetLabel="Send another request"
              />
            ) : (
              <form
                ref={form.formRef}
                className="pk-form"
                onSubmit={form.onSubmit}
                noValidate
              >
                <h2 id="rq-form-title" className="pk-card__title">
                  Project details
                </h2>

                <div className="pk-form__row">
                  <Field
                    id={field('name').id}
                    label="Name"
                    error={errors.name}
                  >
                    <input
                      {...field('name')}
                      className="pk-input"
                      type="text"
                      autoComplete="name"
                      maxLength={LIMITS.name}
                      required
                    />
                  </Field>
                  <Field
                    id={field('email').id}
                    label="Email"
                    error={errors.email}
                  >
                    <input
                      {...field('email')}
                      className="pk-input"
                      type="email"
                      autoComplete="email"
                      inputMode="email"
                      maxLength={LIMITS.email}
                      required
                    />
                  </Field>
                </div>

                <div className="pk-form__row">
                  <Field
                    id={field('phone').id}
                    label="Phone or WhatsApp"
                    optional
                    error={errors.phone}
                  >
                    <input
                      {...field('phone')}
                      className="pk-input"
                      type="tel"
                      autoComplete="tel"
                      inputMode="tel"
                      maxLength={LIMITS.phone}
                    />
                  </Field>
                  <Field
                    id={field('projectType').id}
                    label="Project type"
                    error={errors.projectType}
                  >
                    <select
                      {...field('projectType')}
                      className="pk-select"
                      required
                    >
                      <option value="" disabled>
                        Choose a type
                      </option>
                      {PROJECT_TYPES.map(type => (
                        <option key={type.label} value={type.label}>
                          {type.label}
                        </option>
                      ))}
                    </select>
                  </Field>
                </div>

                <div className="pk-form__row">
                  <Field id={field('budget').id} label="Budget" optional>
                    <select {...field('budget')} className="pk-select">
                      <option value="">Choose a range</option>
                      {BUDGETS.map(budget => (
                        <option key={budget} value={budget}>
                          {budget}
                        </option>
                      ))}
                    </select>
                  </Field>
                  <Field id={field('timeline').id} label="Timeline" optional>
                    <select {...field('timeline')} className="pk-select">
                      <option value="">Choose a timeline</option>
                      {TIMELINES.map(timeline => (
                        <option key={timeline} value={timeline}>
                          {timeline}
                        </option>
                      ))}
                    </select>
                  </Field>
                </div>

                <Field
                  id={field('subject').id}
                  label="Project title"
                  optional
                  error={errors.subject}
                >
                  <input
                    {...field('subject')}
                    className="pk-input"
                    type="text"
                    maxLength={LIMITS.subject}
                    placeholder="e.g. Smart attendance system"
                  />
                </Field>

                <Field
                  id={field('message').id}
                  label="What should it do?"
                  error={errors.message}
                  hint="Main features, technologies you need, and anything your college or client has asked for."
                >
                  <textarea
                    {...field('message', { hint: true })}
                    className="pk-textarea"
                    rows={7}
                    maxLength={LIMITS.message}
                    required
                  />
                </Field>

                <Honeypot inputRef={form.honeypotRef} idPrefix="rq" />

                {submitError && (
                  <div className="pk-alert" data-tone="danger" role="alert">
                    {submitError}
                  </div>
                )}

                <div className="enq-actions">
                  <button
                    type="submit"
                    className="pk-btn pk-btn--primary"
                    aria-busy={sending ? 'true' : undefined}
                    disabled={sending}
                  >
                    <Send aria-hidden="true" />
                    {sending ? 'Sending…' : 'Send request'}
                  </button>
                  <p className="pk-hint">We reply {supportHours()}.</p>
                </div>
              </form>
            )}
          </section>

          <aside className="pk-stack enq-side">
            <div className="pk-card">
              <h2 className="pk-card__title">Project types</h2>
              <p className="pk-muted">
                The same kinds of projects you'll find in the catalog.
              </p>
              <ul className="rq-types">
                {PROJECT_TYPES.map(({ label, icon: Icon }) => (
                  <li key={label}>
                    <Icon aria-hidden="true" />
                    {label}
                  </li>
                ))}
              </ul>
            </div>

            <div className="pk-card pk-card--muted">
              <h2 className="pk-card__title">Prefer WhatsApp?</h2>
              <p className="pk-muted">
                Send us your idea on WhatsApp instead. We reply {supportHours()}.
              </p>
              <a
                className="pk-btn pk-btn--primary pk-btn--block"
                href={WHATSAPP_URL}
                target="_blank"
                rel="noopener noreferrer"
              >
                <MessageCircle aria-hidden="true" />
                Chat on WhatsApp
              </a>
              <a
                className="pk-btn pk-btn--secondary pk-btn--block"
                href={`tel:${contactInfo.phone.primary.replace(/\s/g, '')}`}
              >
                <Phone aria-hidden="true" />
                {contactInfo.phone.display}
              </a>
            </div>
          </aside>
        </div>
      </div>
    </main>
  );
};

export default RequestProjectPage;
